import {useEffect} from "react";
import {useSelector, useStore} from "react-redux";
import {selectUserId} from "../../store/features/Auth/authSlice";
import {setCallChangesLive} from "../../store/features/calls/callChangesSlice";
import {useAppDispatch} from "../../store/store";
import api from "../../store/api";
import {
    CALL_CHANGES_DISABLED_RECHECK_MS,
    CALL_CHANGES_FAILURES_BEFORE_FALLBACK,
    CALL_CHANGES_IDLE_GAP_MS,
    CALL_CHANGES_WAIT_SECONDS,
    callChangeTags,
    getCallChangesRetryDelay,
} from "./callChangesPolling";
import {requestCallChanges} from "./callChangesRequest";

interface Props {
    enabled: boolean;
}

export const CallChangesPoller = ({enabled}: Props) => {
    const userId = useSelector(selectUserId);
    const dispatch = useAppDispatch();
    const {getState} = useStore();

    useEffect(() => {
        if (!enabled || !userId) return;

        let cursor: string | undefined;
        let failureCount = 0;
        let stopped = false;
        let polling = false;
        let pollTimer: number | null = null;
        let activeRequest: AbortController | null = null;

        const clearPollTimer = () => {
            if (pollTimer != null) {
                window.clearTimeout(pollTimer);
                pollTimer = null;
            }
        };

        const schedulePoll = (delay: number) => {
            if (stopped) return;
            clearPollTimer();
            pollTimer = window.setTimeout(() => {
                pollTimer = null;
                void poll();
            }, delay);
        };

        const poll = async () => {
            if (stopped || polling) return;
            polling = true;
            let nextDelay = CALL_CHANGES_IDLE_GAP_MS;
            const request = new AbortController();
            activeRequest = request;

            try {
                const changes = await requestCallChanges(
                    {dispatch, getState},
                    {userId, since: cursor, wait: CALL_CHANGES_WAIT_SECONDS},
                    request.signal,
                );
                if (stopped) return;
                if (changes.changed.length > 0) {
                    dispatch(api.util.invalidateTags(callChangeTags(changes.changed)));
                }
                cursor = changes.cursor;
                failureCount = 0;
                const live = changes.waitSeconds > 0;
                dispatch(setCallChangesLive(live));
                if (!live) nextDelay = CALL_CHANGES_DISABLED_RECHECK_MS;
                else if (changes.changed.length > 0) nextDelay = 0;
            } catch {
                if (stopped) return;
                failureCount += 1;
                if (failureCount >= CALL_CHANGES_FAILURES_BEFORE_FALLBACK) dispatch(setCallChangesLive(false));
                nextDelay = getCallChangesRetryDelay(failureCount);
            } finally {
                if (activeRequest === request) activeRequest = null;
                polling = false;
                schedulePoll(nextDelay);
            }
        };

        const pollImmediately = () => {
            if (stopped || polling) return;
            clearPollTimer();
            void poll();
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') pollImmediately();
        };

        window.addEventListener('online', pollImmediately);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        void poll();

        return () => {
            stopped = true;
            clearPollTimer();
            activeRequest?.abort();
            window.removeEventListener('online', pollImmediately);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            dispatch(setCallChangesLive(false));
        };
    }, [dispatch, enabled, getState, userId]);

    return null;
};
